import { z } from 'zod';
import { IdZ, TeamZ, TsZ } from './common';
import { RatingDeltaZ } from './rating';
import { RulesZ } from './rules';

export const GAME_MODES = ['1v1', '2v2', '3v3', '4v4', '5v5'] as const;
export const GameModeZ = z.enum(GAME_MODES);
export type GameMode = z.infer<typeof GameModeZ>;
export const TEAM_SIZE: Record<GameMode, number> = { '1v1': 1, '2v2': 2, '3v3': 3, '4v4': 4, '5v5': 5 };

/**
 * Tracking depth (stats.md section 3). `final`: only the final score (score_adjust events).
 * `quick`: makes and FTs. `full`: everything the coverage bits allow.
 */
export const GameDepthZ = z.enum(['final', 'quick', 'full']);
export type GameDepth = z.infer<typeof GameDepthZ>;
export const COV = {
  MAKES: 1,
  MISSES: 2,
  SHOT_DETAIL: 4,
  FT: 8,
  REB: 16,
  AST: 32,
  STL: 64,
  BLK: 128,
  TOV: 256,
  PF: 512,
  LINEUP: 1024,
} as const;
export const DEPTH_PRESET: Record<GameDepth, number> = { final: 0, quick: COV.MAKES | COV.FT, full: 2047 };
export const hasCov = (coverage: number, bit: number) => (coverage & bit) === bit;

/** DB status (R6). PAUSED, FINAL_PENDING and the like exist only in the client reducer. */
export const GameStatusZ = z.enum(['live', 'final', 'abandoned']);
export type GameStatus = z.infer<typeof GameStatusZ>;

export const GamePlayerZ = z.object({
  playerId: IdZ,
  team: TeamZ,
  starter: z.boolean(),
});
export type GamePlayer = z.infer<typeof GamePlayerZ>;

export const GameZ = z.object({
  id: IdZ,
  mode: GameModeZ,
  depth: GameDepthZ,
  coverage: z.number().int().min(0).max(2047),
  rules: RulesZ,
  /** Self-scored 1v1: two big buttons, no player pick. */
  honor: z.boolean(),
  status: GameStatusZ,
  /** Cached from the reducer after each accepted batch. */
  scoreA: z.number().int(),
  scoreB: z.number().int(),
  winner: TeamZ.nullable(),
  playedAt: TsZ,
  endedAt: TsZ.nullable(),
  keeperMemberId: IdZ.nullable(),
  keeperDeviceId: IdZ.nullable(),
  keeperEpoch: z.number().int().nonnegative(),
  revision: z.number().int().nonnegative(),
  tournamentId: IdZ.nullable(),
  fixtureId: IdZ.nullable(),
  /** Iso Gauntlet games are real 1v1 games linked to their drill session (R20). */
  drillSessionId: IdZ.nullable(),
  sessionId: IdZ.nullable(),
  createdBy: IdZ,
  createdAt: TsZ,
  deletedAt: TsZ.nullable(),
});
export type Game = z.infer<typeof GameZ>;

export const GameSummaryZ = GameZ.extend({
  players: z.array(GamePlayerZ),
});
export type GameSummary = z.infer<typeof GameSummaryZ>;

export const GameDetailZ = z.object({
  game: GameZ,
  players: z.array(GamePlayerZ),
  /** Highest serverSeq stored for this game (pull cursor). */
  serverSeq: z.number().int().nonnegative(),
  /** Filled by stats once computed (R5). null = not computed yet. */
  deltas: z.array(RatingDeltaZ).nullable(),
});
export type GameDetail = z.infer<typeof GameDetailZ>;

export const KeeperStateZ = z.object({
  keeperMemberId: IdZ.nullable(),
  keeperDeviceId: IdZ.nullable(),
  keeperEpoch: z.number().int().nonnegative(),
});
export type KeeperState = z.infer<typeof KeeperStateZ>;
